import React, { useState, useEffect } from 'react';
import { usePageVisible } from './hooks/usePageVisible';
import {
  Container, Typography, Grid, Paper, Box, Button, Chip, Alert, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Checkbox, useMediaQuery, useTheme,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add, Visibility, ConfirmationNumber, Refresh, ExitToApp, Close as CloseIcon, InfoOutlined } from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import api from './services/api';
import { useManualTrading } from './contexts/ManualTradingContext';
import { useTradingMode } from './contexts/TradingModeContext';
import { LoadingSpinner } from './components/LoadingState';
import EmergencyStopDialog from './components/EmergencyStopDialog';
import ManagePositionsDialog from './components/ManagePositionsDialog';
import { fmt } from './utils/format';
import TradeDetailDialog from './components/TradeDetailDialog';
import type { SettledTrade } from './components/TradeDetailDialog';

interface AccountBalance {
  account_balance: number;
  initial_balance: number;
  daily_pnl: number;
}

interface PositionStats {
  open_positions: number;
  total_positions: number;
  total_unrealized_pnl: number;
  total_realized_pnl: number;
  total_pnl: number;
  total_fees?: number;
  total_net_pnl?: number;
}

interface ReturnHistoryPoint {
  time: string;
  ticker: string;
  realized_pnl: number;
  cumulative_pnl: number;
  trade_source?: string;
  strategy_id?: number | null;
  market_title?: string | null;
  side?: string | null;
  entry_price?: number | null;
  exit_price?: number | null;
  quantity?: number | null;
  close_reason?: string | null;
  entry_time?: string | null;
}

interface ReturnsExtra {
  baseline_pnl: number;
  cumulative_realized: number;
  total_unrealized: number;
  total_pnl: number;
  open_positions: Array<{
    ticker: string;
    entry_time: string | null;
    cost: number;
    unrealized_pnl: number;
    strategy_id: number | null;
    trade_source: string;
    market_title?: string | null;
    side?: string | null;
    entry_price?: number | null;
    quantity?: number | null;
  }>;
}

interface ForecastPosition {
  id: number;
  ticker: string;
  market_title: string | null;
  strategy_id: number | null;
  strategy_name: string;
  entry_price: number;
  current_price: number;
  quantity: number;
  cost: number;
  current_value: number;
  unrealized_pnl: number;
  max_return: number;
  close_time: string | null;
}

interface ForecastSummary {
  total_cost: number;
  current_value: number;
  unrealized_pnl: number;
  weighted_value: number;
  max_value: number;
  max_potential_return: number;
}

interface StrategyInfo {
  id: number;
  name: string;
  is_active: boolean;
  is_system: boolean;
  mode: string | null;
  status: string | null;
  total_pnl: number;
  realized_pnl: number | null;
  unrealized_pnl: number | null;
  open_positions: number | null;
  closed_positions: number | null;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  tokens_consumed: number;
  allocated_capital: number | null;
  deployed_capital: number | null;
}

interface TradeLog {
  id: number;
  timestamp: string;
  strategy_id: number | null;
  ticker: string | null;
  action: string;
  reason: string | null;
  market_data: { title?: string; [key: string]: any } | null;
}

const STRATEGY_COLORS: Record<string, string> = {};
const COLOR_PALETTE = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#06b6d4', '#6366f1', '#f97316', '#14b8a6', '#a855f7'
];
function getStrategyColor(name: string): string {
  if (!STRATEGY_COLORS[name]) {
    STRATEGY_COLORS[name] = COLOR_PALETTE[Object.keys(STRATEGY_COLORS).length % COLOR_PALETTE.length];
  }
  return STRATEGY_COLORS[name];
}

export default function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pageVisible = usePageVisible();
  const { tradeSourceParam } = useManualTrading();
  const { modeParam } = useTradingMode();
  const [balance, setBalance] = useState<AccountBalance | null>(null);
  const [stats, setStats] = useState<PositionStats | null>(null);
  const [returnsHistory, setReturnsHistory] = useState<ReturnHistoryPoint[]>([]);
  const [returnsExtra, setReturnsExtra] = useState<ReturnsExtra>({
    baseline_pnl: 0, cumulative_realized: 0, total_unrealized: 0, total_pnl: 0, open_positions: [],
  });
  const [forecastPositions, setForecastPositions] = useState<ForecastPosition[]>([]);
  const [forecastSummary, setForecastSummary] = useState<ForecastSummary | null>(null);
  const [portfolioSummary, setPortfolioSummary] = useState<ForecastSummary | null>(null);
  const [strategies, setStrategies] = useState<StrategyInfo[]>([]);
  const [tradeLogs, setTradeLogs] = useState<TradeLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailPos, setDetailPos] = useState<ForecastPosition | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState<'6h' | '24h' | '7d' | '30d' | 'all'>('all');
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [showEmergencyStop, setShowEmergencyStop] = useState(false);
  const [showPaperStop, setShowPaperStop] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState<Set<number>>(new Set());
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [chartDetailTrade, setChartDetailTrade] = useState<SettledTrade | null>(null);
  const [showNowDetail, setShowNowDetail] = useState(false);
  const [paperStopLoading, setPaperStopLoading] = useState(false);
  const [paperStopError, setPaperStopError] = useState<string | null>(null);
  const [correlatedWarningDismissed, setCorrelatedWarningDismissed] = useState(false);
  const [dailyNotes, setDailyNotes] = useState<{ summary: string; winners: string[]; losers: string[]; insights: string[] } | null>(null);
  const [dailyNotesLoading, setDailyNotesLoading] = useState(false);
  const [dailyNotesFetched, setDailyNotesFetched] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setRefreshing(true);
    fetchData();
    const interval = setInterval(() => {
      if (!document.hidden) fetchData();
    }, 60000);
    return () => clearInterval(interval);
  }, [tradeSourceParam, timeRange, modeParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh immediately when tab becomes visible again
  useEffect(() => {
    if (pageVisible && lastRefresh) fetchData();
  }, [pageVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const fetchDailyNotes = async () => {
    if (dailyNotesFetched) return;
    setDailyNotesLoading(true);
    try {
      const headers: Record<string, string> = { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` };
      const res = await fetch(`/api/v1/ai/daily-notes?mode=${modeParam}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDailyNotes(data);
        setDailyNotesFetched(true);
      }
    } catch {
      // Non-critical — don't block dashboard
    } finally {
      setDailyNotesLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('access_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const timeParam = timeRange === '6h' ? '&hours=6' : timeRange === '24h' ? '&days=1' : timeRange === '7d' ? '&days=7' : timeRange === '30d' ? '&days=30' : '';
      const tradingModeFilter = modeParam === 'paper' ? 'paper' : 'live';
      const queryStr = `?trade_source=${tradeSourceParam}${timeParam}&mode=${tradingModeFilter}`;

      // Full portfolio summary (all trade sources) for the balance tile
      const portfolioQueryStr = `?trade_source=all${timeParam}&mode=${tradingModeFilter}`;

      const [balanceRes, statsRes, returnsRes, forecastRes, portfolioRes, stratRes, logsRes, syncRes] = await Promise.all([
        fetch(`/api/v1/account/balance?mode=${modeParam}`, { headers }),
        fetch(`/api/v1/positions/stats/summary${queryStr}`, { headers }),
        fetch(`/api/v1/positions/stats/returns-history${queryStr}`, { headers }),
        fetch(`/api/v1/positions/stats/forecasted-by-day${queryStr}`, { headers }),
        fetch(`/api/v1/positions/stats/forecasted-by-day${portfolioQueryStr}`, { headers }),
        api.get(`/api/v1/strategies/?trade_source=${tradeSourceParam}${timeParam}&mode=${modeParam}`),
        fetch('/api/v1/autonomous/log?limit=20', { headers }),
        fetch('/api/v1/kalshi/sync-status', { headers }),
      ]);

      if (balanceRes.ok) setBalance(await balanceRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (returnsRes.ok) {
        const data = await returnsRes.json();
        setReturnsHistory(data.history || []);
        setReturnsExtra({
          baseline_pnl: data.baseline_pnl || 0,
          cumulative_realized: data.cumulative_realized || 0,
          total_unrealized: data.total_unrealized || 0,
          total_pnl: data.total_pnl || 0,
          open_positions: data.open_positions || [],
        });
      }
      if (forecastRes.ok) {
        const data = await forecastRes.json();
        setForecastPositions(data.positions || []);
        setForecastSummary(data.summary || null);
        setSelectedPositions(new Set());
      }
      if (portfolioRes.ok) {
        const data = await portfolioRes.json();
        setPortfolioSummary(data.summary || null);
      }
      setStrategies(stratRes.data || []);
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setTradeLogs(logsData.logs || []);
      }
      if (syncRes.ok) {
        const syncData = await syncRes.json();
        setLastSync(syncData.last_sync || null);
      }
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <LoadingSpinner />
      </Container>
    );
  }

  // Compute hero P&L from displayed strategies so it matches tile sum
  const realizedPnl = strategies.reduce((sum, s) => sum + (s.realized_pnl || 0), 0);
  const unrealizedPnl = strategies.reduce((sum, s) => sum + (s.unrealized_pnl || 0), 0);
  const totalPnl = realizedPnl + unrealizedPnl;
  const openCount = strategies.reduce((sum, s) => sum + (s.open_positions || 0), 0);

  // Build Portfolio P&L chart data — backend handles time filtering with correct baseline
  const useShortLabels = timeRange === '6h' || timeRange === '24h';
  const parseTime = (t: string) => t.endsWith('Z') || t.includes('+') ? new Date(t) : new Date(t + 'Z');

  const returnsChartData: Array<{
    label: string;
    total_pnl: number;
    realized_pnl: number;
    trade_pnl?: number;
    isNow?: boolean;
    originalPoint?: ReturnHistoryPoint;
  }> = returnsHistory.map((point) => {
    const d = parseTime(point.time);
    const label = useShortLabels
      ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return {
      label,
      total_pnl: point.cumulative_pnl,
      realized_pnl: point.cumulative_pnl,
      trade_pnl: point.realized_pnl,
      originalPoint: point,
    };
  });

  // If there are open positions but no closed history, add an origin point
  if (returnsChartData.length === 0 && returnsExtra.open_positions.length > 0) {
    const entries = returnsExtra.open_positions
      .filter((p) => p.entry_time)
      .map((p) => new Date(p.entry_time!).getTime());
    const earliest = entries.length > 0 ? Math.min(...entries) : Date.now();
    const d = new Date(earliest);
    returnsChartData.push({
      label: useShortLabels
        ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      total_pnl: returnsExtra.baseline_pnl,
      realized_pnl: returnsExtra.baseline_pnl,
    });
  }

  // Append "Now" point showing total P&L including unrealized
  const hasOpenPositions = returnsExtra.open_positions.length > 0;
  if (hasOpenPositions || returnsChartData.length > 0) {
    const lastRealized = returnsChartData.length > 0
      ? returnsChartData[returnsChartData.length - 1].realized_pnl
      : returnsExtra.baseline_pnl;
    const nowTotal = lastRealized + returnsExtra.total_unrealized;
    returnsChartData.push({
      label: 'Now',
      total_pnl: nowTotal,
      realized_pnl: lastRealized,
      isNow: true,
    });
  }

  const sortedPositions = [...forecastPositions].sort((a, b) => {
    if (!a.close_time && !b.close_time) return 0;
    if (!a.close_time) return 1;
    if (!b.close_time) return -1;
    return new Date(a.close_time).getTime() - new Date(b.close_time).getTime();
  });

  // Show all strategies: user-created + system (manual) strategies with capital or activity
  const userStrategies = strategies.filter(s =>
    !s.is_system
    || (s.allocated_capital || 0) > 0
    || (s.open_positions || 0) + (s.closed_positions || 0) > 0
    || (s.total_pnl || 0) !== 0
  );
  // When viewing a specific mode, only show strategies matching that mode
  const modeFilteredStrategies = modeParam === 'live'
    ? userStrategies.filter(s => s.mode === 'live')
    : userStrategies.filter(s => s.mode !== 'live');
  const liveStrategies = modeFilteredStrategies.filter(s => s.mode === 'live');
  // Strategies that are no longer live but still have open live positions
  const windingDown = modeFilteredStrategies.filter(s => s.mode !== 'live' && (s.open_positions || 0) > 0);
  // Paper strategies split by budget status
  const allPaperStrategies = modeFilteredStrategies.filter(s => s.mode !== 'live' && (s.open_positions || 0) === 0);
  const paperStrategies = allPaperStrategies.filter(s => (s.allocated_capital || 0) > 0);
  // No-budget paper strategies — only show if they have some activity (trades or P&L)
  const noBudgetStrategies = allPaperStrategies.filter(s =>
    (s.allocated_capital || 0) === 0 &&
    ((s.total_pnl || 0) !== 0 || (s.closed_positions || 0) > 0 || s.winning_trades + s.losing_trades > 0)
  );

  const getStrategyName = (id: number | null) => {
    if (!id) return 'Unknown';
    const s = strategies.find(s => s.id === id);
    return s ? s.name : `Strategy ${id}`;
  };

  const handleChartDotClick = (payload: any) => {
    if (payload?.isNow) {
      setShowNowDetail(true);
    } else if (payload?.originalPoint) {
      setChartDetailTrade(payload.originalPoint);
    }
  };

  const renderStrategyCard = (s: StrategyInfo) => {
    const pnl = s.total_pnl || 0;
    const realized = s.realized_pnl || 0;
    const unrealized = s.unrealized_pnl || 0;
    const deployed = s.deployed_capital || 0;
    const budget = s.allocated_capital || 0;
    const closedTrades = s.winning_trades + s.losing_trades;
    const winRate = closedTrades > 0 ? ((s.winning_trades / closedTrades) * 100).toFixed(0) : null;
    const openCount = s.open_positions || 0;
    const closedCount = s.closed_positions ?? closedTrades;
    return (
      <Grid item xs={12} sm={6} md={4} key={s.id}>
        <Paper
          sx={{
            p: 2.5,
            cursor: 'pointer',
            borderLeft: `4px solid ${getStrategyColor(s.name)}`,
            '&:hover': { bgcolor: 'action.hover' },
            height: '100%',
            overflow: 'hidden',
          }}
          onClick={() => navigate(`/strategies/${s.id}/detail`)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, mr: 1 }}>{s.is_system ? 'Manual Trades' : s.name}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
              {budget > 0 && (
                <Chip
                  label={`$${fmt(budget, 0)}`}
                  size="small"
                  sx={{ fontSize: 10, fontWeight: 700, backgroundColor: 'rgba(122,95,78,0.1)', color: '#7A5F4E' }}
                />
              )}
            </Box>
          </Box>
          <Box aria-live="polite" aria-atomic="true">
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>P&L</Typography>
            <Typography
              sx={{ fontWeight: 700, mb: 0.5, fontSize: 'clamp(1rem, 3vw, 1.5rem)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              color={pnl >= 0 ? 'success.main' : 'error.main'}
            >
              {pnl >= 0 ? '+' : ''}${fmt(pnl)}
            </Typography>
            {/* P&L breakdown: realized from closed + unrealized from open */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Closed: <span style={{ color: realized >= 0 ? '#27AE60' : '#C0392B', fontWeight: 600 }}>
                  {realized >= 0 ? '+' : ''}${fmt(realized)}
                </span>
              </Typography>
              {openCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Open: <span style={{ color: unrealized >= 0 ? '#27AE60' : '#C0392B', fontWeight: 600 }}>
                    {unrealized >= 0 ? '+' : ''}${fmt(unrealized)}
                  </span>
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Positions</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{openCount} open / {closedCount} closed</Typography>
            </Box>
            {deployed > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">At Risk</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>${fmt(deployed)}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Win Rate</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{winRate ? `${winRate}%` : 'No closed trades'}</Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ position: 'relative' }}>
      {/* Loading overlay when switching trade source / time range */}
      {refreshing && !loading && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(253,246,236,0.7)', zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 1,
        }}>
          <CircularProgress size={40} sx={{ color: '#3A8C8C' }} />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Home</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            sx={{ color: '#7A5F4E' }}
            aria-label="Refresh dashboard"
          >
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => navigate('/strategies/create')}
            sx={{ textTransform: 'none', backgroundColor: '#3A8C8C', '&:hover': { backgroundColor: '#2A6666' } }}
          >
            New Strategy
          </Button>
        </Box>
      </Box>

      {/* Time Range Filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {(['6h', '24h', '7d', '30d', 'all'] as const).map((range) => (
          <Chip
            key={range}
            label={range === 'all' ? 'All Time' : range.toUpperCase()}
            size="small"
            onClick={() => setTimeRange(range)}
            sx={{
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
              backgroundColor: timeRange === range ? '#3A8C8C' : 'transparent',
              color: timeRange === range ? '#fff' : 'text.secondary',
              border: timeRange === range ? 'none' : '1px solid',
              borderColor: timeRange === range ? 'transparent' : 'divider',
              '&:hover': {
                backgroundColor: timeRange === range ? '#2A6666' : 'action.hover',
              },
            }}
          />
        ))}
      </Box>

      {/* Correlated Market Warning */}
      {(() => {
        // Detect correlated positions: group by event prefix (ticker without the last threshold segment)
        // Kalshi tickers follow patterns like EVENT-DATE-TVALUE, where multiple tickers share EVENT-DATE
        const getEventPrefix = (ticker: string): string => {
          // Remove last -T... or -W... segment (threshold/bucket markers)
          const match = ticker.match(/^(.+?)(-T\d+|-W\d+.*|-B\d+.*)$/);
          return match ? match[1] : ticker;
        };
        const eventGroups: Record<string, { tickers: string[]; titles: string[] }> = {};
        forecastPositions.forEach((pos) => {
          const prefix = getEventPrefix(pos.ticker);
          if (!eventGroups[prefix]) eventGroups[prefix] = { tickers: [], titles: [] };
          eventGroups[prefix].tickers.push(pos.ticker);
          if (pos.market_title) eventGroups[prefix].titles.push(pos.market_title);
        });
        const correlatedEvents = Object.entries(eventGroups).filter(([, g]) => g.tickers.length > 1);

        if (correlatedEvents.length === 0) return null;
        return (
          <Collapse in={!correlatedWarningDismissed}>
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  size="small"
                  onClick={() => setCorrelatedWarningDismissed(true)}
                  aria-label="Dismiss correlated market warning"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Correlated Positions Detected
              </Typography>
              <Typography variant="body2">
                You have multiple positions on the same event
                {correlatedEvents.length === 1 ? '' : 's'}:{' '}
                {correlatedEvents.map(([prefix, g]) => (
                  <span key={prefix}>
                    <strong>{prefix}</strong> ({g.tickers.length} positions)
                  </span>
                )).reduce<React.ReactNode[]>((acc, el, i) => {
                  if (i === 0) return [el];
                  return [...acc, ', ', el];
                }, [])}
                . Correlated markets may amplify risk if the event outcome affects all positions similarly.
              </Typography>
            </Alert>
          </Collapse>
        );
      })()}

      {/* Hero Metrics */}
      <Grid container spacing={3} data-tour="dashboard-overview">
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, borderLeft: `4px solid ${modeParam === 'live' ? '#C0392B' : '#3A8C8C'}`, overflow: 'hidden', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {modeParam === 'live'
                ? 'Cash Balance'
                : 'Paper Balance'}
            </Typography>
            <Box aria-live="polite" aria-atomic="true">
              {(() => {
                const cash = balance?.account_balance || 0;
                const dailyPnl = balance?.daily_pnl || 0;
                return (
                  <>
                    <Typography sx={{ fontWeight: 700, fontSize: 'clamp(1.25rem, 4vw, 2.5rem)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ${fmt(cash)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Today: {dailyPnl >= 0 ? '+' : ''}${fmt(dailyPnl)}
                    </Typography>
                  </>
                );
              })()}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          {(() => {
            const positionCost = modeFilteredStrategies.reduce((sum, s) => sum + (s.deployed_capital || 0), 0);
            return (
              <Paper sx={{ p: 3, borderLeft: '4px solid #E8A045', overflow: 'hidden', height: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Money at Risk</Typography>
                <Box aria-live="polite" aria-atomic="true">
                  <Typography sx={{ fontWeight: 700, fontSize: 'clamp(1.25rem, 4vw, 2.5rem)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    ${fmt(positionCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {openCount} {openCount === 1 ? 'position' : 'positions'} across {modeFilteredStrategies.filter(s => (s.open_positions || 0) > 0).length} {modeFilteredStrategies.filter(s => (s.open_positions || 0) > 0).length === 1 ? 'strategy' : 'strategies'}
                  </Typography>
                </Box>
                {lastSync && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.6rem' }}>
                    Last sync: {(() => {
                      try {
                        const d = new Date(lastSync.endsWith('Z') || lastSync.includes('+') ? lastSync : lastSync + 'Z');
                        return isNaN(d.getTime()) ? 'Unknown' : d.toLocaleString();
                      } catch { return 'Unknown'; }
                    })()}
                  </Typography>
                )}
              </Paper>
            );
          })()}
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, borderLeft: `4px solid ${totalPnl >= 0 ? '#27AE60' : '#C0392B'}`, overflow: 'hidden', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {stats?.total_fees > 0 ? 'Net P&L' : 'Total P&L'}
            </Typography>
            <Box aria-live="polite" aria-atomic="true">
              <Typography sx={{ fontWeight: 700, fontSize: 'clamp(1.25rem, 4vw, 2.5rem)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} color={(stats?.total_fees > 0 ? (stats.total_net_pnl ?? (totalPnl - stats.total_fees)) : totalPnl) >= 0 ? 'success.main' : 'error.main'}>
                {stats?.total_fees > 0
                  ? `${(stats.total_net_pnl ?? (totalPnl - stats.total_fees)) >= 0 ? '+' : ''}$${fmt(stats.total_net_pnl ?? (totalPnl - stats.total_fees))}`
                  : `${totalPnl >= 0 ? '+' : ''}$${fmt(totalPnl)}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ${realizedPnl >= 0 ? '+' : ''}{fmt(realizedPnl)} realized, ${unrealizedPnl >= 0 ? '+' : ''}{fmt(unrealizedPnl)} open
              </Typography>
              {stats?.total_fees > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Gross: ${fmt(totalPnl)} | Fees: -${fmt(stats.total_fees)}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #7A5F4E', overflow: 'hidden', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Active Strategies</Typography>
            <Box aria-live="polite" aria-atomic="true">
              <Typography sx={{ fontWeight: 700, fontSize: 'clamp(1.25rem, 4vw, 2.5rem)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {modeFilteredStrategies.filter(s => s.is_active).length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {modeParam === 'live' ? 'Live' : 'Paper'} mode
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Portfolio P&L — shows realized (settled) + unrealized (open) P&L */}
      {returnsChartData.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Performance</Typography>
            {lastRefresh && (
              <Typography variant="caption" color="text.secondary">
                Last refresh: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
          {/* Realized / Unrealized / Total breakdown */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <MuiTooltip title="Locked-in profit or loss from positions that have been closed and settled" arrow>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                Settled <InfoOutlined sx={{ fontSize: 14, opacity: 0.6 }} />:&nbsp;
                <span style={{ color: returnsExtra.cumulative_realized >= 0 ? '#27AE60' : '#C0392B', fontWeight: 600 }}>
                  {returnsExtra.cumulative_realized >= 0 ? '+' : ''}${fmt(returnsExtra.cumulative_realized)}
                </span>
              </Typography>
            </MuiTooltip>
            {returnsExtra.total_unrealized !== 0 && (
              <MuiTooltip title="Current estimated value of positions still open — changes with market prices until settled" arrow>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                  Open positions <InfoOutlined sx={{ fontSize: 14, opacity: 0.6 }} />:&nbsp;
                  <span style={{ color: returnsExtra.total_unrealized >= 0 ? '#27AE60' : '#C0392B', fontWeight: 600 }}>
                    {returnsExtra.total_unrealized >= 0 ? '+' : ''}${fmt(returnsExtra.total_unrealized)}
                  </span>
                </Typography>
              </MuiTooltip>
            )}
            <MuiTooltip title="Settled + Open positions combined. Settled P&L is locked in; Open P&L fluctuates until positions close." arrow>
              <Typography variant="body2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                Total <InfoOutlined sx={{ fontSize: 14, opacity: 0.6 }} />:&nbsp;
                <span style={{ color: returnsExtra.total_pnl >= 0 ? '#27AE60' : '#C0392B' }}>
                  {returnsExtra.total_pnl >= 0 ? '+' : ''}${fmt(returnsExtra.total_pnl)}
                </span>
              </Typography>
            </MuiTooltip>
          </Box>
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
            <LineChart data={returnsChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0E4CE" />
              <XAxis dataKey="label" stroke="#7A5F4E" tick={{ fontSize: 11 }} />
              <YAxis stroke="#7A5F4E" tickFormatter={(v) => `$${v.toFixed(0)}`} />
              <ReferenceLine y={0} stroke="#7A5F4E" strokeDasharray="3 3" />
              <Tooltip
                contentStyle={{ backgroundColor: '#FDF6EC', border: '1px solid #F0E4CE' }}
                formatter={(value: number, name: string) => {
                  if (value === undefined || value === null) return [null, null];
                  return [`$${value.toFixed(2)}`, name];
                }}
                labelFormatter={(label, payload) => {
                  if (label === 'Now') return 'Current (includes open positions)';
                  const point = payload?.[0]?.payload?.originalPoint;
                  if (point?.time) {
                    const d = parseTime(point.time);
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  }
                  return label;
                }}
                itemStyle={{ fontSize: 12 }}
              />
              <Legend />
              {/* Main line: total P&L including unrealized at the "Now" point */}
              <Line
                type="stepAfter"
                dataKey="total_pnl"
                name="Total P&L"
                stroke={returnsExtra.total_pnl >= 0 ? '#27AE60' : '#C0392B'}
                strokeWidth={2}
                dot={(props: any) => {
                  const { cx, cy, payload, index } = props;
                  if (!cx || !cy) return <circle r={0} />;
                  if (payload?.isNow) {
                    const color = payload.total_pnl >= 0 ? '#27AE60' : '#C0392B';
                    return <circle key={`total-${index}`} cx={cx} cy={cy} r={6} fill={color} stroke="#fff" strokeWidth={2} style={{ cursor: 'pointer' }} onClick={() => handleChartDotClick(payload)} />;
                  }
                  return <circle key={`total-${index}`} cx={cx} cy={cy} r={3} fill={returnsExtra.total_pnl >= 0 ? '#27AE60' : '#C0392B'} style={{ cursor: 'pointer' }} onClick={() => handleChartDotClick(payload)} />;
                }}
                activeDot={false}
              />
              {/* Realized P&L line — only diverges from total at "Now" when unrealized ≠ 0 */}
              {returnsExtra.total_unrealized !== 0 && (
                <Line
                  type="stepAfter"
                  dataKey="realized_pnl"
                  name="Settled P&L"
                  stroke="#10b981"
                  strokeWidth={1}
                  strokeDasharray="4 2"
                  dot={false}
                />
              )}
              {/* Per-trade P&L as individual dots */}
              <Line
                type="monotone"
                dataKey="trade_pnl"
                name="Per-Trade P&L"
                stroke="#3b82f6"
                strokeWidth={0}
                dot={(props: any) => {
                  const { cx, cy, payload, index } = props;
                  if (!cx || !cy || payload?.trade_pnl == null) return <circle key={`trade-${index}`} r={0} />;
                  return <circle key={`trade-${index}`} cx={cx} cy={cy} r={3} fill="#3b82f6" style={{ cursor: 'pointer' }} onClick={() => handleChartDotClick(payload)} />;
                }}
                activeDot={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* My Live Strategies */}
      {liveStrategies.length > 0 && (
        <>
          <Box data-tour="dashboard-strategies-card" sx={{ mt: 3, mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              My Live Strategies
              <Chip label={liveStrategies.length} size="small" sx={{ ml: 1.5, fontWeight: 700, backgroundColor: 'rgba(192,57,43,0.12)', color: '#C0392B' }} />
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {liveStrategies.map(renderStrategyCard)}
          </Grid>
        </>
      )}

      {/* Winding Down — inactive strategies with remaining open positions */}
      {windingDown.length > 0 && (
        <>
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Winding Down
              <Chip label={windingDown.length} size="small" sx={{ ml: 1.5, fontWeight: 700, backgroundColor: 'rgba(232,160,69,0.15)', color: '#E8A045' }} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No longer active, but still have open positions awaiting settlement
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {windingDown.map(renderStrategyCard)}
          </Grid>
        </>
      )}

      {/* My Paper Strategies */}
      {paperStrategies.length > 0 && (
        <>
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              My Paper Strategies
              <Chip label={paperStrategies.length} size="small" sx={{ ml: 1.5, fontWeight: 700, backgroundColor: 'rgba(58,140,140,0.12)', color: '#3A8C8C' }} />
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {paperStrategies.map(renderStrategyCard)}
          </Grid>
        </>
      )}

      {/* No-Budget Paper Strategies (less prominent) */}
      {noBudgetStrategies.length > 0 && (
        <>
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              No Budget Assigned
              <Chip label={noBudgetStrategies.length} size="small" sx={{ ml: 1.5, fontWeight: 700, backgroundColor: 'rgba(122,95,78,0.1)', color: '#7A5F4E' }} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Assign a budget on the Strategies page to move these into your active paper strategies
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 0.5, opacity: 0.75 }}>
            {noBudgetStrategies.map(renderStrategyCard)}
          </Grid>
        </>
      )}

      {/* Empty state */}
      {strategies.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No strategies yet. Create your first AI-powered trading strategy to get started.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/strategies/create')}>
            Create Strategy
          </Button>
        </Paper>
      )}

      {/* Daily Performance Notes */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Daily Notes</Typography>
          {!dailyNotes && !dailyNotesLoading && (
            <Button
              size="small"
              variant="outlined"
              onClick={fetchDailyNotes}
              disabled={dailyNotesLoading}
            >
              Generate
            </Button>
          )}
          {dailyNotesLoading && <CircularProgress size={20} />}
        </Box>
        {!dailyNotes && !dailyNotesLoading && (
          <Typography variant="body2" color="text.secondary">
            Get an AI summary of what worked and what didn't in the last 24 hours.
          </Typography>
        )}
        {dailyNotes && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>{dailyNotes.summary}</Typography>
            {dailyNotes.winners.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 0.5 }}>What Worked</Typography>
                {dailyNotes.winners.map((w, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 1, mb: 0.25 }}>&bull; {w}</Typography>
                ))}
              </Box>
            )}
            {dailyNotes.losers.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>What Didn't</Typography>
                {dailyNotes.losers.map((l, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 1, mb: 0.25 }}>&bull; {l}</Typography>
                ))}
              </Box>
            )}
            {dailyNotes.insights.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="info.main" sx={{ mb: 0.5 }}>Takeaways</Typography>
                {dailyNotes.insights.map((ins, i) => (
                  <Typography key={i} variant="body2" sx={{ ml: 1, mb: 0.25 }}>&bull; {ins}</Typography>
                ))}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              Past performance does not guarantee future results.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Trade Execution Log */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>Recent Activity</Typography>
        {tradeLogs.length === 0 ? (
          <Alert severity="info">
            No trade logs yet. Logs will appear here once your strategies start placing trades.
          </Alert>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {tradeLogs.map((log) => {
              const md = log.market_data || {};
              const isBuy = log.action === 'ENTER';
              const qty = md.quantity || 0;
              const price = isBuy ? md.price : md.exit_price;
              const cost = qty && price ? qty * price : null;
              const pnl = !isBuy && md.entry_price && md.exit_price ? (md.exit_price - md.entry_price) * qty * (md.side === 'no' ? -1 : 1) : md.pnl;
              const returnPct = md.return_pct;
              return (
                <Box
                  key={log.id}
                  sx={{
                    borderLeft: 3,
                    borderColor: isBuy ? 'success.main' : 'info.main',
                    bgcolor: 'action.hover',
                    p: 1.5, mb: 1, borderRadius: 1
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" gap={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {(log.timestamp.endsWith('Z') || log.timestamp.includes('+') ? new Date(log.timestamp) : new Date(log.timestamp + 'Z')).toLocaleTimeString()}
                      </Typography>
                      <Chip label={isBuy ? 'BUY' : 'SELL'} size="small"
                        color={isBuy ? 'success' : 'default'}
                        sx={{ fontSize: 11, fontWeight: 600 }}
                      />
                    </Box>
                    <Box display="flex" gap={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getStrategyColor(getStrategyName(log.strategy_id)), flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {getStrategyName(log.strategy_id)}
                      </Typography>
                    </Box>
                  </Box>
                  {(md.title || log.ticker) && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {md.title || log.ticker}
                    </Typography>
                  )}
                  {md.title && log.ticker && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {log.ticker}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {isBuy && cost != null && (
                      <>Cost: <strong>${fmt(cost)}</strong></>
                    )}
                    {!isBuy && cost != null && (
                      <>Sold: <strong>${fmt(cost)}</strong></>
                    )}
                    {!isBuy && pnl != null && (
                      <> &middot; P&L: <span style={{ color: pnl >= 0 ? '#27AE60' : '#C0392B', fontWeight: 600 }}>
                        {pnl >= 0 ? '+' : ''}${fmt(pnl)}{returnPct != null ? ` (${returnPct.toFixed(1)}%)` : ''}
                      </span></>
                    )}
                    {isBuy && md.edge != null && (
                      <> &middot; Edge: {(md.edge * 100).toFixed(1)}%</>
                    )}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Open Positions */}
      {sortedPositions.length > 0 && forecastSummary && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">
              Open Positions ({sortedPositions.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {modeParam === 'live' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setShowEmergencyStop(true)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Stop All
                </Button>
              )}
              {modeParam === 'paper' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => { setPaperStopError(null); setShowPaperStop(true); }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Stop All
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<ExitToApp />}
                disabled={selectedPositions.size === 0}
                onClick={() => setShowManageDialog(true)}
                sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#3A8C8C', '&:hover': { backgroundColor: '#2A6666' } }}
              >
                Manage Selected ({selectedPositions.size})
              </Button>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 42 }}>
                    <Checkbox
                      size="small"
                      checked={selectedPositions.size === sortedPositions.length && sortedPositions.length > 0}
                      indeterminate={selectedPositions.size > 0 && selectedPositions.size < sortedPositions.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPositions(new Set(sortedPositions.map(p => p.id)));
                        } else {
                          setSelectedPositions(new Set());
                        }
                      }}
                      sx={{ color: '#7A5F4E', '&.Mui-checked': { color: '#3A8C8C' }, '&.MuiCheckbox-indeterminate': { color: '#3A8C8C' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Market</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Strategy</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Side</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>Cost</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728' }}>Expires</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#4A3728' }}>If Correct</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4A3728', width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPositions.map((pos) => {
                  const cost = pos.cost ?? pos.entry_price * pos.quantity;
                  const upside = pos.max_return ?? 0;
                  const closeDateObj = pos.close_time ? new Date(pos.close_time.endsWith('Z') ? pos.close_time : pos.close_time + 'Z') : null;
                  const closeLabel = closeDateObj
                    ? closeDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : 'TBD';
                  const stratName = pos.strategy_name === 'Unknown' ? 'Unassigned' : pos.strategy_name;
                  const isSelected = selectedPositions.has(pos.id);
                  return (
                    <TableRow key={pos.id} hover selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedPositions(prev => {
                              const next = new Set(prev);
                              if (next.has(pos.id)) next.delete(pos.id);
                              else next.add(pos.id);
                              return next;
                            });
                          }}
                          sx={{ color: '#7A5F4E', '&.Mui-checked': { color: '#3A8C8C' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, maxWidth: { xs: 120, sm: 220 } }}>
                        <Typography variant="body2" noWrap>{pos.market_title || pos.ticker}</Typography>
                        {pos.market_title && (
                          <Typography variant="caption" noWrap sx={{ color: '#7A5F4E', display: 'block', fontSize: '0.65rem' }}>
                            {pos.ticker}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getStrategyColor(stratName), flexShrink: 0 }} />
                          <Typography variant="body2" noWrap>{stratName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pos.entry_price < 0.5 ? 'No' : 'Yes'}
                          size="small"
                          sx={{ fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>${fmt(cost)}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{closeLabel}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600, color: upside > 0 ? '#27AE60' : 'inherit' }}>
                        {upside > 0 ? `+$${fmt(upside)}` : '-'}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setDetailPos(pos)} sx={{ color: '#7A5F4E' }} aria-label="View position details">
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell sx={{ borderTop: '2px solid #F0E4CE' }}></TableCell>
                  <TableCell colSpan={3} sx={{ fontWeight: 700, borderTop: '2px solid #F0E4CE' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, borderTop: '2px solid #F0E4CE' }}>
                    ${fmt(forecastSummary.total_cost)}
                  </TableCell>
                  <TableCell sx={{ borderTop: '2px solid #F0E4CE' }}></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#27AE60', borderTop: '2px solid #F0E4CE' }}>
                    +${fmt(forecastSummary.max_potential_return)}
                  </TableCell>
                  <TableCell sx={{ borderTop: '2px solid #F0E4CE' }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Disclaimer below positions table */}
          <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: '1px solid #E8A045', bgcolor: 'rgba(232,160,69,0.08)' }}>
            <Typography variant="body2" sx={{ color: '#7A5F4E' }}>
              <strong>Note:</strong> Quantato is a software platform, not a broker. The fastest way to exit a position is to go directly
              to <a href="https://kalshi.com" target="_blank" rel="noopener noreferrer" style={{ color: '#3A8C8C', fontWeight: 600 }}>kalshi.com</a>.
              Exit orders sent through Quantato are routed through your own Kalshi API connection, but execution is not instant and depends on market liquidity and Kalshi API availability.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Position Detail Modal */}
      <Dialog open={!!detailPos} onClose={() => setDetailPos(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { backgroundColor: '#FDF6EC', border: '1px solid #F0E4CE' } }}>
        {detailPos && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>
              {detailPos.market_title || detailPos.ticker}
              {detailPos.market_title && (
                <Typography variant="caption" sx={{ color: '#7A5F4E', display: 'block' }}>
                  {detailPos.ticker}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {[
                  { label: 'Strategy', value: detailPos.strategy_name === 'Unknown' ? 'Unassigned' : detailPos.strategy_name },
                  { label: 'Ticker', value: detailPos.ticker },
                  { label: 'Entry Price', value: `$${fmt(detailPos.entry_price)}` },
                  { label: 'Current Price', value: `$${fmt(detailPos.current_price)}` },
                  { label: 'Quantity', value: detailPos.quantity },
                  { label: 'Cost', value: `$${fmt(detailPos.cost ?? detailPos.entry_price * detailPos.quantity)}` },
                  { label: 'Current Value', value: `$${fmt(detailPos.current_value ?? 0)}` },
                  { label: 'Unrealized P&L', value: `${(detailPos.unrealized_pnl ?? 0) >= 0 ? '+' : ''}$${fmt(detailPos.unrealized_pnl ?? 0)}` },
                  { label: 'Max Return (If Correct)', value: detailPos.max_return > 0 ? `+$${fmt(detailPos.max_return)}` : '-' },
                  { label: 'Closes', value: detailPos.close_time ? new Date(detailPos.close_time.endsWith('Z') ? detailPos.close_time : detailPos.close_time + 'Z').toLocaleString() : 'TBD' },
                ].map((item) => (
                  <Grid item xs={6} key={item.label}>
                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailPos(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Paper Stop Confirmation Dialog */}
      <Dialog
        open={showPaperStop}
        onClose={() => { if (!paperStopLoading) setShowPaperStop(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#FDF6EC', border: '1px solid #F0E4CE' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Close All Paper Positions?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will close <strong>{openCount}</strong> paper position{openCount !== 1 ? 's' : ''} at
            current market prices. Paper balance will be updated with realized P&L.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live trading is not affected.
          </Typography>
          {paperStopError && (
            <Alert severity="error" sx={{ mt: 2 }}>{paperStopError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPaperStop(false)}
            disabled={paperStopLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={paperStopLoading}
            onClick={async () => {
              setPaperStopLoading(true);
              setPaperStopError(null);
              try {
                await api.post('/api/v1/autonomous/emergency-stop', {
                  mode: 'paper',
                  include_manual: true,
                });
                setShowPaperStop(false);
                fetchData();
              } catch (err: any) {
                setPaperStopError(err.response?.data?.detail || 'Failed to stop paper trades');
              } finally {
                setPaperStopLoading(false);
              }
            }}
          >
            {paperStopLoading ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <EmergencyStopDialog
        open={showEmergencyStop}
        onClose={() => setShowEmergencyStop(false)}
        onConfirmed={() => { setShowEmergencyStop(false); fetchData(); }}
        openPositions={openCount}
        atRiskAmount={forecastSummary?.total_cost || 0}
      />

      <ManagePositionsDialog
        open={showManageDialog}
        onClose={() => setShowManageDialog(false)}
        positions={sortedPositions.filter(p => selectedPositions.has(p.id))}
        onSuccess={() => {
          setShowManageDialog(false);
          setSelectedPositions(new Set());
          fetchData();
        }}
      />

      <TradeDetailDialog
        trade={chartDetailTrade}
        showNow={showNowDetail}
        openPositions={returnsExtra.open_positions}
        totalUnrealized={returnsExtra.total_unrealized}
        getStrategyName={getStrategyName}
        onClose={() => { setChartDetailTrade(null); setShowNowDetail(false); }}
      />
    </Container>
  );
}
